import type {
  PackageConfig,
  PackageConfigsIndex,
  PackageEstimate,
  PackageModuleRow,
  PepPackagePricingRule,
  PackageProfileDefinition,
  PackageProvisioningPlan,
  PackageSelectionState,
  PricingGridsResponse,
  ProductCard,
} from "@itaas/pep-contracts";
import { formatProvisioningPlan, resolvePackageProvisioning } from "@itaas/pep-contracts";
import {
  activeBillingDimensions,
  billingDimensionAnchors,
  dimensionHint,
  dimensionLabel,
  dimensionMinQty,
  formatPricingUnitLabel,
  initialQuantitiesForPackage,
  lowQuantityMessage,
  needsLowQuantityConfirmation,
  primarySeatCount,
  quantityForModule,
  resolveBillingDimensions,
} from "./billing-dimensions";

export type { PackageProvisioningPlan, PackageSelectionState };

export function buildProvisioningPlan(
  state: PackageSelectionState,
  index: PackageConfigsIndex,
): PackageProvisioningPlan {
  return resolvePackageProvisioning(state, index);
}

const PLATFORM_MODULES = new Set(["iam", "suite", "notifications"]);

function moduleRole(pkg: PackageConfig, slug: string): PackageModuleRow["role"] {
  if (pkg.requiredModules.includes(slug)) return "required";
  if (pkg.recommendedModules.includes(slug)) return "recommended";
  return "optional";
}

export function buildModuleRows(
  pkg: PackageConfig,
  products: ProductCard[],
  selectedModules: string[],
): PackageModuleRow[] {
  const slugs = [
    ...pkg.requiredModules,
    ...pkg.recommendedModules,
    ...pkg.optionalModules,
  ];
  const bySlug = new Map(products.map((p) => [p.slug, p]));

  return slugs.map((slug) => {
    const role = moduleRole(pkg, slug);
    const locked = role === "required";
    return {
      slug,
      role,
      locked,
      selected: locked || selectedModules.includes(slug),
      product: bySlug.get(slug),
    };
  });
}

export function initialSelectionForPackage(pkg: PackageConfig): PackageSelectionState {
  const selected = new Set([...pkg.requiredModules, ...pkg.recommendedModules]);

  for (const moduleId of Object.keys(pkg.moduleProfiles)) {
    selected.add(moduleId);
  }

  const moduleProfiles: Record<string, string[]> = {};
  for (const [moduleId, profileIds] of Object.entries(pkg.moduleProfiles)) {
    moduleProfiles[moduleId] = [...profileIds];
  }

  const quantities = initialQuantitiesForPackage(pkg);

  return {
    packageSlug: pkg.slug,
    selectedModules: [...selected],
    moduleProfiles,
    quantities,
    seatCount: primarySeatCount(quantities),
  };
}

export function setQuantity(
  state: PackageSelectionState,
  key: string,
  value: number,
): PackageSelectionState {
  const quantities = { ...state.quantities, [key]: Math.max(1, value) };
  return { ...state, quantities, seatCount: primarySeatCount(quantities) };
}

export function toggleModule(
  state: PackageSelectionState,
  pkg: PackageConfig,
  slug: string,
  on: boolean,
): PackageSelectionState {
  if (pkg.requiredModules.includes(slug)) return state;

  let selectedModules = on
    ? [...new Set([...state.selectedModules, slug])]
    : state.selectedModules.filter((s) => s !== slug);

  const moduleProfiles = { ...state.moduleProfiles };

  if (!on) {
    delete moduleProfiles[slug];
  } else if (pkg.moduleProfiles[slug]?.length) {
    moduleProfiles[slug] = [...pkg.moduleProfiles[slug]];
  }

  return { ...state, selectedModules, moduleProfiles };
}

export function toggleProfile(
  state: PackageSelectionState,
  pkg: PackageConfig,
  index: PackageConfigsIndex,
  moduleId: string,
  profileId: string,
  on: boolean,
): PackageSelectionState {
  const def = index.profileCatalog[profileId];
  if (!def || def.required) return state;

  const current = new Set(state.moduleProfiles[moduleId] ?? []);
  if (on) current.add(profileId);
  else current.delete(profileId);

  let selectedModules = [...state.selectedModules];
  if (on && !selectedModules.includes(moduleId)) {
    selectedModules.push(moduleId);
  }

  if (on && def.suggestsModule && !selectedModules.includes(def.suggestsModule)) {
    selectedModules = [...new Set([...selectedModules, def.suggestsModule])];
  }

  const moduleProfiles = { ...state.moduleProfiles, [moduleId]: [...current] };

  if (!moduleProfiles[moduleId]?.length) {
    delete moduleProfiles[moduleId];
    if (!pkg.requiredModules.includes(moduleId)) {
      selectedModules = selectedModules.filter((s) => s !== moduleId);
    }
  }

  return { ...state, selectedModules, moduleProfiles };
}

function billableModules(modules: string[]): string[] {
  return modules.filter((s) => !PLATFORM_MODULES.has(s));
}

function moduleUnitPrice(slug: string, grids: PricingGridsResponse) {
  const row = grids.products[slug];
  const price = row?.professional ?? row?.starter ?? 0;
  return { price: price ?? 0, unit: row?.unit ?? "user/month" };
}

function moduleBundleFactor(slug: string, rule?: PepPackagePricingRule): number {
  const factor = rule?.moduleDiscounts?.[slug];
  if (factor == null) return 1;
  return Math.max(0, Math.min(1, factor));
}

export function estimatePackagePrice(
  state: PackageSelectionState,
  index: PackageConfigsIndex,
  grids: PricingGridsResponse,
  locale: "fr" | "en" = "fr",
): PackageEstimate {
  const breakdown: PackageEstimate["breakdown"] = [];
  let monthlyUsd = 0;
  const pkg = index.packages.find((p) => p.slug === state.packageSlug);
  if (!pkg) {
    return {
      monthlyUsd: 0,
      monthlyUsdFormatted: "Sur devis",
      seatCount: state.seatCount,
      quantities: { ...state.quantities },
      breakdown: [],
      disclaimer: "",
    };
  }

  const packageRule = grids.packageRules?.[pkg.slug];

  for (const slug of billableModules(state.selectedModules)) {
    const { price, unit } = moduleUnitPrice(slug, grids);
    if (!price || unit.includes("included")) continue;
    const qty = quantityForModule(slug, state.quantities, grids, pkg);
    const factor = moduleBundleFactor(slug, packageRule);
    const amount = price * qty * factor;
    if (amount <= 0) continue;
    monthlyUsd += amount;
    const unitLabel = formatPricingUnitLabel(unit, locale);
    breakdown.push({
      label:
        factor < 1
          ? `${slug} — ${qty} ${unitLabel} × $${price} × ${Math.round(factor * 100)}%`
          : `${slug} — ${qty} ${unitLabel} × $${price}`,
      amountUsd: amount,
    });
  }

  for (const [moduleId, profileIds] of Object.entries(state.moduleProfiles)) {
    if (!state.selectedModules.includes(moduleId)) continue;
    for (const profileId of profileIds) {
      const def = index.profileCatalog[profileId];
      if (!def?.seatUsdAddon) continue;
      const qty = quantityForModule(moduleId, state.quantities, grids, pkg);
      const addon = def.seatUsdAddon * qty * moduleBundleFactor(moduleId, packageRule);
      monthlyUsd += addon;
      const unit = grids.products[moduleId]?.unit ?? "user/month";
      const unitLabel = formatPricingUnitLabel(unit, locale);
      breakdown.push({
        label:
          locale === "en"
            ? `${def.labelEn ?? def.labelFr} (+$${def.seatUsdAddon}/${unitLabel})`
            : `${def.labelFr} (+$${def.seatUsdAddon}/${unitLabel})`,
        amountUsd: addon,
      });
    }
  }

  const formatter = new Intl.NumberFormat(locale === "en" ? "en-US" : "fr-FR", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  return {
    monthlyUsd,
    monthlyUsdFormatted: monthlyUsd > 0 ? `${formatter.format(monthlyUsd)}/mois` : "Sur devis",
    seatCount: state.seatCount,
    quantities: { ...state.quantities },
    breakdown,
    disclaimer:
      locale === "en"
        ? "Indicative estimate per billing metric — commercial validation required."
        : "Estimation indicative par métrique de facturation — validation commerciale requise.",
  };
}

export function profilesForModule(
  pkg: PackageConfig,
  index: PackageConfigsIndex,
  moduleId: string,
): Array<PackageProfileDefinition & { id: string }> {
  const ids = new Set([
    ...(pkg.moduleProfiles[moduleId] ?? []),
    ...(pkg.optionalProfileAddons?.[moduleId] ?? []),
  ]);
  return [...ids]
    .map((id) => ({ id, ...index.profileCatalog[id] }))
    .filter((p): p is PackageProfileDefinition & { id: string } => Boolean(p.moduleId));
}

export function formatPackageSummary(
  pkg: PackageConfig,
  state: PackageSelectionState,
  index: PackageConfigsIndex,
  estimate: PackageEstimate,
  locale: "fr" | "en",
): string {
  const name = locale === "en" ? pkg.displayNameEn ?? pkg.displayNameFr : pkg.displayNameFr;
  const lines = [
    locale === "en" ? `Package: ${name}` : `Package : ${name}`,
  ];

  for (const dim of resolveBillingDimensions(pkg, state.selectedModules)) {
    const qty = state.quantities[dim.key] ?? dim.defaultQty;
    lines.push(
      locale === "en"
        ? `${dimensionLabel(dim, locale)}: ${qty}`
        : `${dimensionLabel(dim, locale)} : ${qty}`,
    );
  }

  lines.push(
    locale === "en"
      ? `Modules: ${state.selectedModules.join(", ")}`
      : `Modules : ${state.selectedModules.join(", ")}`,
  );

  for (const [moduleId, profileIds] of Object.entries(state.moduleProfiles)) {
    const labels = profileIds.map((id) => index.profileCatalog[id]?.labelFr ?? id);
    lines.push(
      locale === "en"
        ? `Profiles ${moduleId}: ${labels.join(", ")}`
        : `Profils ${moduleId} : ${labels.join(", ")}`,
    );
  }

  lines.push(
    locale === "en"
      ? `Estimate: ${estimate.monthlyUsdFormatted}`
      : `Estimation : ${estimate.monthlyUsdFormatted}`,
  );

  const provisioning = resolvePackageProvisioning(state, index);
  lines.push(...formatProvisioningPlan(provisioning, index, locale));

  return lines.join("\n");
}

export {
  activeBillingDimensions,
  billingDimensionAnchors,
  dimensionHint,
  dimensionLabel,
  dimensionMinQty,
  lowQuantityMessage,
  needsLowQuantityConfirmation,
  resolveBillingDimensions,
};
